/**
 * Browser audio utilities for the Gemini Live voice interview.
 *
 * - `openMicrophoneStream` captures the microphone and re-emits it as raw
 *   signed 16-bit little-endian PCM at 16 kHz (the format Gemini Live expects
 *   for `audio/pcm;rate=16000`).
 * - `PcmPlayer` plays back Gemini's 24 kHz PCM16 output over the speakers with
 *   barge-in aware flushing.
 * - The pure helpers (`float32ToPcm16`, `pcm16ToFloat32`, `base64ToPcm16`)
 *   are intentionally framework-free so they can be unit tested.
 */

export const MIC_SAMPLE_RATE = 16000;
export const OUTPUT_SAMPLE_RATE = 24000;

/** Converts a Float32Array of audio samples ([-1, 1]) into raw PCM16 LE bytes. */
export function float32ToPcm16(data: Float32Array): Uint8Array {
  const out = new Uint8Array(data.length * 2);
  for (let i = 0; i < data.length; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    const sample = s < 0 ? s * 0x8000 : s * 0x7fff;
    out[i * 2] = sample & 0xff;
    out[i * 2 + 1] = (sample >> 8) & 0xff;
  }
  return out;
}

/** Converts a PCM16LE byte buffer into Float32 samples in [-1, 1]. */
export function pcm16ToFloat32(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(Math.floor(bytes.length / 2));
  for (let i = 0; i < out.length; i++) {
    let sample = bytes[i * 2] | (bytes[i * 2 + 1] << 8);
    if (sample > 0x7fff) sample -= 0x10000;
    out[i] = sample / 32768;
  }
  return out;
}

/** Decodes a base64 string of PCM16LE samples into an Int16Array. */
export function base64ToPcm16(base64: string): Int16Array {
  const binary = atob(base64);
  const out = new Int16Array(Math.floor(binary.length / 2));
  for (let i = 0; i < out.length; i++) {
    out[i] = binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8);
  }
  return out;
}

/** Base64-encodes a buffer of PCM16LE bytes (for Gemini Live realtime input). */
export function pcm16ToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** A live handle to an open microphone converted to raw 16 kHz PCM16LE. */
export interface MicStreamHandle {
  chunks: AsyncGenerator<Uint8Array, void, void>;
  stop: () => void;
}

/**
 * Opens the microphone and yields raw PCM16LE chunks at 16 kHz.
 *
 * The AudioContext is created synchronously (before the permission await) so it
 * stays within the user gesture that triggered `start()`, which keeps the
 * audio pipeline running under browser autoplay policies.
 */
export async function openMicrophoneStream(): Promise<MicStreamHandle> {
  const audioContext = new AudioContext({ sampleRate: MIC_SAMPLE_RATE });
  void audioContext.resume();

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: MIC_SAMPLE_RATE,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (err) {
    await audioContext.close();
    throw err;
  }

  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  const silentSink = audioContext.createGain();
  silentSink.gain.value = 0;

  source.connect(processor);
  processor.connect(silentSink);
  silentSink.connect(audioContext.destination);

  const queue: Uint8Array[] = [];
  const waiters: Array<() => void> = [];
  let closed = false;

  const wake = () => {
    const pending = waiters.splice(0);
    pending.forEach((resolve) => resolve());
  };

  processor.onaudioprocess = (event) => {
    if (closed) return;
    const data = event.inputBuffer.getChannelData(0);
    queue.push(float32ToPcm16(data));
    wake();
  };

  async function* chunks(): AsyncGenerator<Uint8Array, void, void> {
    while (!closed) {
      if (queue.length > 0) {
        yield queue.shift()!;
      } else {
        await new Promise<void>((resolve) => waiters.push(resolve));
      }
    }
  }

  const stop = () => {
    if (closed) return;
    closed = true;
    processor.onaudioprocess = null;
    try {
      source.disconnect();
      processor.disconnect();
      silentSink.disconnect();
    } catch {
      // Already disconnected — safe to ignore.
    }
    stream.getTracks().forEach((track) => track.stop());
    void audioContext.close();
    wake();
  };

  return { chunks: chunks(), stop };
}

/**
 * Plays back 24 kHz PCM16 audio from Gemini Live through the speakers.
 *
 * Incoming base64 audio frames are pushed into a ring buffer; a
 * ScriptProcessorNode pulls samples in real time. `interrupt()` drops whatever
 * has not been played yet so barge-in (the model's response being cut off) does
 * not keep playing stale audio.
 *
 * Playback is gated by a prebuffer: audio is not decoded to the speakers until
 * about one callback window (~170 ms) is queued, and the gate is re-armed every
 * time the buffer drains. Without it, ScriptProcessorNode starves at the start
 * of every utterance and frames arriving over a jittery network get rendered as
 * choppy, broken-sounding speech.
 */
export class PcmPlayer {
  private context: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private buffer: Int16Array | null = null;
  private head = 0;
  private tail = 0;
  private started = false;

  /** Callback invoked when the audio buffer drains completely after having played. */
  onDrain: (() => void) | null = null;

  /** Samples to lead in before playback begins (≈170 ms at 24 kHz). */
  private static readonly PREBUFFER_SAMPLES = 4096;

  /** Number of samples currently queued in the ring buffer. */
  buffered(): number {
    const cap = this.buffer?.length ?? 0;
    if (cap === 0) return 0;
    return (this.tail - this.head + cap) % cap;
  }

  /** Whether audio is currently actively playing or queued to play. */
  isPlaying(): boolean {
    return this.started || this.buffered() > 0;
  }

  /** Creates (or resumes) the AudioContext. Call early, inside a user gesture. */
  ensureContext(): AudioContext {
    if (this.context) {
      void this.context.resume();
      return this.context;
    }
    this.context = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
    const processor = this.context.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (event) => this.pull(event.outputBuffer);
    processor.connect(this.context.destination);
    this.processor = processor;
    // Initial capacity for 60 seconds of 24 kHz audio.
    this.buffer = new Int16Array(OUTPUT_SAMPLE_RATE * 60);
    return this.context;
  }

  /** Queues a frame of PCM16LE samples for playback, expanding dynamically if needed. */
  pushPcm16(bytes: Int16Array): void {
    if (!this.buffer) return;
    const currentBuffered = this.buffered();
    const needed = currentBuffered + bytes.length + 1;
    if (needed > this.buffer.length) {
      // Expand buffer dynamically so samples are NEVER overwritten on long utterances
      const newCap = Math.max(this.buffer.length * 2, needed + OUTPUT_SAMPLE_RATE * 10);
      const newBuf = new Int16Array(newCap);
      for (let i = 0; i < currentBuffered; i++) {
        newBuf[i] = this.buffer[(this.head + i) % this.buffer.length];
      }
      this.buffer = newBuf;
      this.head = 0;
      this.tail = currentBuffered;
    }
    const cap = this.buffer.length;
    for (let i = 0; i < bytes.length; i++) {
      this.buffer[this.tail] = bytes[i];
      this.tail = (this.tail + 1) % cap;
      if (this.tail === this.head) this.head = (this.head + 1) % cap;
    }
  }

  /** Drops all queued audio that has not been played yet (barge-in flush). */
  interrupt(): void {
    this.head = this.tail;
    this.started = false;
  }

  /** Stops playback, closes the context, and releases the graph. */
  stop(): void {
    this.onDrain = null;
    if (this.processor) {
      this.processor.onaudioprocess = null;
      try {
        this.processor.disconnect();
      } catch {
        // No-op.
      }
      this.processor = null;
    }
    if (this.context) {
      void this.context.close();
      this.context = null;
    }
    this.buffer = null;
    this.head = 0;
    this.tail = 0;
    this.started = false;
  }

  private pull(output: AudioBuffer): void {
    const data = output.getChannelData(0);
    const cap = this.buffer?.length ?? 0;
    for (let i = 0; i < data.length; i++) {
      if (!this.buffer || cap === 0) {
        data[i] = 0;
        continue;
      }
      if (this.head === this.tail) {
        if (this.started) {
          this.started = false;
          this.onDrain?.();
        }
        data[i] = 0;
        continue;
      }
      if (!this.started) {
        // Do not consume samples while we are filling the prebuffer so the
        // speaker never starts on a half-empty queue.
        if (this.buffered() < PcmPlayer.PREBUFFER_SAMPLES) {
          data[i] = 0;
          continue;
        }
        this.started = true;
      }
      data[i] = this.buffer[this.head] / 32768;
      this.head = (this.head + 1) % cap;
    }
  }
}